import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { AddTeamMemberComponent } from './add-team-member/add-team-member.component';
import {
  combineLatest,
  finalize,
  map,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import {
  CaseDetailModel,
  CaseMember,
  ManageStudentDrawerParams,
} from '../../insights.interface';
import { InsightMode } from '../../insights.enums';
import { DrawerService } from '../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { CaseMemberService } from './case-member.service';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzFormModule } from 'ng-zorro-antd/form';
import { FormsModule } from '@angular/forms';
import { LoaderService } from '../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { CommonService } from '../../../../shared/services/common.service';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import { ErrorResponse } from '../../../../core/models/error.models';
import { SearchInputComponent } from '../../../../components/search-input/search-input.component';
import { AuditContextService } from '../../../../features/audit-logs/audit-context.service';
import { DrawerHeaderComponent } from '../../../../components/drawer-header/drawer-header.component';

@Component({
  selector: 'app-manage-student-team',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzButtonModule,
    NzInputModule,
    NzIconModule,
    NzTableModule,
    NzCheckboxModule,
    CommonModule,
    NzRadioModule,
    NzFormModule,
    FormsModule,
    NzSpinModule,
    ConfirmDialogComponent,
    SearchInputComponent,
    DrawerHeaderComponent,
  ],
  templateUrl: './manage-student-team.component.html',
})
export class ManageStudentTeamComponent implements OnDestroy {
  private readonly caseMemberService = inject(CaseMemberService);
  private readonly loaderService = inject(LoaderService);
  private readonly auditContextService = inject(AuditContextService);

  private readonly destroy$ = new Subject<void>();
  @ViewChild('addTeamMemberDrawerTitleTpl')
  addTeamMemberDrawerTitleTpl?: TemplateRef<object>;

  currentSUverviosr: CaseMember | null = null;
  loading$: Observable<boolean> = of(false);
  caseMemberList$ = combineLatest([
    this.caseMemberService.caseMemberList$,
    this.loaderService.loading$,
  ]).pipe(
    map(([response]) => {
      if (response?.result) {
        this.totalRows = response.result.totalRows;
        this.currentSUverviosr =
          response.result.items.find(member => member.isSupervisor) || null;
      }
      return response?.result?.items || [];
    })
  );

  searchTerm = '';
  caseParams: ManageStudentDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.VIEW,
  };

  listOfColumn = [
    {
      title: 'Name',
      key: 'caseMemberName',
    },
    {
      title: 'Organization Name',
      key: 'organizationName',
    },
    {
      title: 'Supervisor',
      key: 'isSupervisor',
    },
    {
      title: '',
    },
  ];

  pageIndex = 1;
  totalRows = 0;
  currentPage = 1;
  pageSize = 10;
  sortOrder: number | undefined = undefined;
  sortField = '';

  showConfirmDialog = false;
  isDeleting = false;
  selectedMemberId: number | null = null;
  confrimDialogMsg = 'Are you sure you want to delete this member?';

  constructor(
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.setupDrawerSubscription();
    this.loading$ = this.loaderService.loading$;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, pageIndex, sort } = params;
    this.pageSize = pageSize;
    this.pageIndex = pageIndex;

    const currentSort = sort.find(item => item.value !== null);

    if (currentSort) {
      this.sortField = currentSort.key;
      this.sortOrder = currentSort.value === 'ascend' ? 1 : 0;
    } else {
      this.sortField = '';
      this.sortOrder = undefined;
    }
    this.loadCaseMembers();
  }

  handleSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.loadCaseMembers();
  }

  private loadCaseMembers(): void {
    this.loaderService.show();
    this.caseMemberService
      .getCaseMemberList({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        caseId: this.caseParams.case?.id ?? 0,
        searchTerm: this.searchTerm,
        sortOrder: this.sortOrder ?? 0,
        sortField: this.sortField,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loaderService.hide(),
        error: () => this.loaderService.hide(),
      });
  }

  onUpdateMember(data: CaseMember): void {
    const originalValue = data.isSupervisor;
    this.loaderService.show();

    // Build audit log modifiedFields using AuditContextService

    const oldValue = {
      isSupervisor: data.isSupervisor,
      caseMemberName: this.currentSUverviosr?.caseMemberName || '',
    };
    const newValue = {
      isSupervisor: true,
      caseMemberName: data.caseMemberName,
    };
    const modifiedFields = this.auditContextService.buildFieldChanges(
      oldValue,
      newValue,
      {
        sensitiveFields: [],
        labelMap: {
          isSupervisor: 'Supervisor',
          caseMemberName: 'Name',
        },
        excludeFields: ['id'],
      }
    );

    const payload = {
      isSupervisor: true,
      _modifiedFields: modifiedFields,
    };

    this.caseMemberService
      .updateCaseMember(this.caseParams.case?.id ?? 0, data.id, payload)
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'Team member updated successfully'
          );
          this.loaderService.hide();
          this.caseParams.case!.supervisorName = data.caseMemberName;
          this.currentSUverviosr = data;
        },
        error: ({ errors, message }: ErrorResponse) => {
          data.isSupervisor = originalValue;
          this.cdr.detectChanges();
          const errorMessages = errors?.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify(
            'error',
            errorMessages?.length
              ? errorMessages
              : ['Failed to update team member'],
            message
          );
          this.loaderService.hide();
        },
      });
  }

  onDeleteMember(memberId: number): void {
    this.selectedMemberId = memberId;
    this.showConfirmDialog = true;
  }

  handleDelete(): void {
    if (!this.selectedMemberId) return;

    this.isDeleting = true;

    this.caseMemberService
      .deleteCaseMember(this.selectedMemberId, this.caseParams.case?.id ?? 0)
      .pipe(
        finalize(() => {
          this.isDeleting = false;
          this.loaderService.hide();
          this.showConfirmDialog = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'Team member deleted successfully'
          );
          this.selectedMemberId = null;
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors?.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify(
            'error',
            errorMessages?.length
              ? errorMessages
              : ['Failed to delete team member'],
            message
          );
        },
      });
  }

  handleCancel(): void {
    this.showConfirmDialog = false;
    this.selectedMemberId = null;
  }

  openAddTeamMember(): void {
    this.caseMemberList$.pipe(take(1)).subscribe(memberList => {
      this.drawerService.openDrawer(
        AddTeamMemberComponent,
        this.addTeamMemberDrawerTitleTpl || 'Add Team Member',
        1170,
        true,
        undefined,
        {
          case: this.caseParams.case,
          mode: this.caseParams.mode,
          memberList,
        },
        DRAWER_IDS.ADD_TEAM_MEMBER
      );
    });
  }

  closeAddTeamMemberDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.ADD_TEAM_MEMBER);
  }

  getStudentFullName(): string {
    const firstName = this.caseParams.case?.student?.firstName ?? '';
    const lastName = this.caseParams.case?.student?.lastName ?? '';
    return `${firstName} ${lastName}`.trim();
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageStudentDrawerParams>(DRAWER_IDS.BIP_MANGE_STUDENT_TEAM)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
        }
      });
  }
}
